import type { AppRouter, RouterInputs, RouterOutputs } from '@databink/core/api';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink, TRPCClientError } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';

/**
 * The API client. The app is typed against the server's router, so a changed procedure is a
 * compile error here rather than a broken screen. Every request carries the header the
 * server's CSRF guard asks of cookie-authenticated calls.
 */

export type { RouterInputs, RouterOutputs };
export type RecordList = RouterOutputs['records']['list'];
export type RecordView = RouterOutputs['records']['get'];

let unauthenticated: (() => void) | null = null;

/**
 * What to do when a read comes back 401: the session ended (expired, signed out elsewhere, or
 * ended by a password change). Only reads: a mutation's 401 can be a wrong current password.
 */
export function onUnauthenticated(handler: () => void): void {
  unauthenticated = handler;
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (errorStatus(error) === 401) unauthenticated?.();
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failures, error) => failures < 2 && errorStatus(error) >= 500,
    },
  },
});

export const trpcClient = createTRPCClient<AppRouter>({
  links: [httpBatchLink({ url: '/trpc', headers: () => ({ 'x-databink-csrf': '1' }) })],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({ client: trpcClient, queryClient });

export interface ApiIssue {
  path: string;
  message: string;
}

function clientError(error: unknown): TRPCClientError<AppRouter> | null {
  return error instanceof TRPCClientError ? (error as TRPCClientError<AppRouter>) : null;
}

/** The HTTP status of a failed call; 0 when it never reached the server. */
export function errorStatus(error: unknown): number {
  const data = clientError(error)?.data as { httpStatus?: number } | undefined;
  return data?.httpStatus ?? 0;
}

export function errorMessage(error: unknown): string {
  if (!error) return '';
  const trpcError = clientError(error);
  if (trpcError) return trpcError.message;
  if (error instanceof Error) return error.message;
  return String(error);
}

/** Field problems the server reported, keyed by field path. */
export function errorIssues(error: unknown): ApiIssue[] {
  const data = clientError(error)?.data as { issues?: ApiIssue[] } | undefined;
  return Array.isArray(data?.issues) ? data.issues : [];
}

/** The message without the list of issues the server appends, for when issues are shown beside fields. */
export function errorSummary(error: unknown): string {
  const message = errorMessage(error);
  return errorIssues(error).length > 0 ? message.replace(/:.*$/s, '') : message;
}
